import { signIn } from "@bindery/auth-consumer/browser";
import { Button } from "@bindery/ui/components/ui/button";
import { Skeleton } from "@bindery/ui/components/ui/skeleton";
import type { ReactNode } from "react";
import type { SessionState } from "../data/session";
import { useSession } from "../data/use-session";

type SignedInSession = Extract<SessionState, { status: "signed-in" }>;

export function Prompt({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="flex max-w-prose flex-col gap-4">
      <h1 className="font-serif text-3xl font-semibold tracking-tight">
        {title}
      </h1>
      <div className="text-muted-foreground flex flex-col gap-2">
        {children}
      </div>
      {action === undefined ? null : <div>{action}</div>}
    </section>
  );
}

export function LoadingLines() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-live="polite">
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-4 w-full max-w-md" />
      <Skeleton className="h-4 w-3/4 max-w-sm" />
    </div>
  );
}

/** Renders the page for an adult’s session, and a way in for everyone else. */
export function SignedIn({
  returnTo,
  children,
}: {
  returnTo?: string;
  children: (session: SignedInSession) => ReactNode;
}) {
  const session = useSession();
  switch (session.status) {
    case "loading":
      return <LoadingLines />;
    case "signed-in":
      return <>{children(session)}</>;
    case "signed-out":
      return (
        <Prompt
          title="Sign in to make worksheets"
          action={<Button onClick={() => signIn(returnTo)}>Sign in</Button>}
        >
          <p>
            A worksheet is one printed page of sentences for a reader you are
            linked to. Sign in with the account that linked them.
          </p>
        </Prompt>
      );
    case "forbidden":
      return (
        <Prompt
          title="Sign in as an adult"
          action={
            <Button onClick={() => signIn(returnTo)}>
              Sign in as an adult
            </Button>
          }
        >
          <p>
            This sign-in belongs to a reader. Worksheets are made, printed and
            graded by the adult who is linked to them.
          </p>
        </Prompt>
      );
    case "unreachable":
      return (
        <Prompt
          title="Couldn’t check who is signed in"
          action={
            <Button variant="outline" onClick={() => window.location.reload()}>
              Try again
            </Button>
          }
        >
          <p>The sign-in service didn’t answer. Reload to try again.</p>
        </Prompt>
      );
  }
}
